import { useState } from 'react';
import { CaretRight, PlayCircle, X } from '@phosphor-icons/react';
import Modal from '../../../components/molecules/Modal/Modal';
import useEvents from '../../../hooks/useEvents';
import LinkingOptionButton from './LinkingOptionButton';

const YOUTUBE_EMBED_URL = 'https://www.youtube.com/embed/5aQmaEnXhME';

const VIDEO_TITLE = '¿Cómo vincular tu cuenta del SAT?';

const VideoFrame = ({ className }: { className: string }) => (
	<div className={className}>
		<iframe
			src={YOUTUBE_EMBED_URL}
			title={VIDEO_TITLE}
			allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
			allowFullScreen
			className='w-full h-full'
		/>
	</div>
);

/** La portada del paso 1: el video incrustado dentro de la tarjeta. */
export const LinkingVideoCover = () => (
	<VideoFrame className='aspect-[9/16] w-1/2 max-w-[160px] mx-auto z-10 overflow-hidden rounded-2xl border border-h-neutral-90 bg-black shadow-lg' />
);

/** Desde dónde se abrió el video. `card` es la tarjeta suelta debajo del
 * formulario; `help_modal` es la fila dentro del modal de ayuda. Va en el
 * payload para poder separar las dos superficies en el mismo evento. */
type VideoSource = 'card' | 'help_modal';

/** Estado + tracking del video: lo comparten la tarjeta y la fila del modal
 * para que el evento `linking_help_video` sea uno solo, con `source` distinto. */
const useLinkingHelpVideo = (
	source: VideoSource,
	onOpenChange?: (open: boolean) => void,
) => {
	const [open, setOpen] = useState(false);
	const { trackEvent } = useEvents();

	const track = (actionType: 'viewed' | 'close') =>
		trackEvent({
			eventName: 'linking_help_video',
			eventPayload: { action_type: actionType, source },
		});

	const show = () => {
		setOpen(true);
		onOpenChange?.(true);
		track('viewed');
	};

	const close = () => {
		setOpen(false);
		onOpenChange?.(false);
		track('close');
	};

	return { open, show, close };
};

const LinkingHelpVideoModal = ({
	open,
	onClose,
}: {
	open: boolean;
	onClose: () => void;
}) => (
	<Modal
		open={open}
		onClose={onClose}
		showCloseButton={false}
		sx='w-[90vw] max-w-lg !p-0 overflow-hidden'
	>
		<button
			onClick={onClose}
			className='absolute top-2 right-2 z-30 bg-h-primary text-white rounded-full p-1.5'
		>
			<X size={20} weight='bold' />
		</button>
		<VideoFrame className='aspect-[9/16] w-full' />
	</Modal>
);

/**
 * Fila del video para las superficies de ayuda (ModalFailed). Va PRIMERA en el
 * modal: es la ayuda más rápida de consumir y la que menos texto exige.
 *
 * `onOpenChange` avisa al modal de abajo para que se tape (`hidden`) mientras
 * el video está encima: cerrarlo desmontaría su contenido y dejarlo visible
 * enseña dos paneles y dos X encimadas.
 */
export const LinkingHelpVideoOption = ({
	onOpenChange,
}: {
	onOpenChange?: (open: boolean) => void;
}) => {
	const { open, show, close } = useLinkingHelpVideo('help_modal', onOpenChange);

	return (
		<>
			<LinkingHelpVideoModal open={open} onClose={close} />
			<LinkingOptionButton
				icon={<PlayCircle size={22} weight='fill' />}
				text={
					<span className='flex min-w-0 flex-col'>
						<span className='text-h-text-heading text-sm leading-snug font-medium'>
							{VIDEO_TITLE}
						</span>
						<span className='text-h-medium text-xs font-light'>
							Ver video explicativo
						</span>
					</span>
				}
				action={show}
			/>
		</>
	);
};

/**
 * Card que abre el video explicativo en modal.
 *
 * Vive aquí y no dentro del paso 1 porque el brazo de tratamiento del
 * experimento de entrada aterriza directo en el paso 2 sin pasar por la
 * portada. Sin un componente compartido ese brazo se quedaría sin video, y el
 * experimento estaría midiendo dos cambios (saltarse el selector Y quitar el
 * video) en lugar de uno.
 */
const LinkingHelpVideo = () => {
	const { open, show, close } = useLinkingHelpVideo('card');

	return (
		<>
			<LinkingHelpVideoModal open={open} onClose={close} />

			<button
				onClick={show}
				className='bg-white rounded-xl shadow px-4 py-3 flex items-center gap-3 w-full text-left'
			>
				<PlayCircle size={28} weight='fill' className='text-h-primary shrink-0' />
				<div className='flex-1 min-w-0'>
					<p className='font-medium text-sm'>{VIDEO_TITLE}</p>
					<p className='text-xs text-h-medium font-light'>Ver video explicativo</p>
				</div>
				<CaretRight size={18} weight='bold' className='text-h-primary shrink-0' />
			</button>
		</>
	);
};

export default LinkingHelpVideo;
